// src/utils/intentionStats.ts
//
// Single source of truth for teaching-intention response/submission statistics.
//
// Historically this stat was computed independently on /intentions, Review
// Intentions, the dashboard, Intention Periods and Reports — with different
// numerators (some counted drafts as submissions), different denominators (some
// divided by ALL active staff, some by "people who started the form") and
// different rounding, so the same period showed 91% in one place and 89.9% in
// another. Everything now funnels through `computeIntentionResponseStats`.
//
// Canonical definitions
// ---------------------
//   Expected  = active staff in a teaching role (see TEACHING_ROLES). Admin /
//               principal / support staff are not asked to submit a teaching
//               intention and are excluded (pass staffGroup: 'all' to include
//               every active staff member instead).
//   Submitted = a distinct expected staff member with at least one intention
//               whose status is NOT 'draft' (a saved-but-unsubmitted draft is
//               NOT a response). Each person counts at most once.
//   Rate      = submitted / expected, rounded to a whole percentage.

/** Minimal staff shape this module needs (decoupled from the full StaffMember). */
export interface StaffLike {
  role?: string | null;
  status?: string | null;
  staff_code?: string | null;
  initial?: string | null;
  staff_id?: string | null;
}

/** Minimal intention shape this module needs. */
export interface IntentionLike {
  staffId?: string | null;
  status?: string | null;
  intentionToReturn?: 'yes' | 'no' | 'uncertain' | null;
  targetFTE?: number | null;
}

/** Roles expected to submit a teaching intention (admin/principal excluded). */
export const TEACHING_ROLES = [
  'teacher',
  'curriculum_leader',
  'head_of_department',
  'assistant_principal',
];

/**
 * teaching_intentions.staff_id holds the 3-letter staff CODE, so we match the
 * roster on staff_code (manual) → initial (auto) → staff_id, normalised.
 */
export function getStaffCode(s: StaffLike): string {
  return (s.staff_code || s.initial || s.staff_id || '').toString().trim().toUpperCase();
}

/** Whether a staff member is expected to submit an intention. */
export function isExpectedToSubmit(
  staff: StaffLike,
  staffGroup: 'teaching' | 'all' = 'teaching'
): boolean {
  if (staff.status && staff.status !== 'active') return false;
  if (staffGroup === 'all') return true;
  return TEACHING_ROLES.includes(staff.role || 'teacher');
}

/** A submitted intention is any intention that is not a draft. */
export function isSubmittedStatus(status: string | null | undefined): boolean {
  return !!status && status !== 'draft';
}

// ---------------------------------------------------------------------------
// Shared status + return-intention presentation (one vocabulary app-wide)
// ---------------------------------------------------------------------------

export interface IntentionStatusMeta {
  /** Human label for the badge. */
  label: string;
  /** Design-token background class. */
  bg: string;
  /** Design-token text class. */
  text: string;
  /** Semantic tone. */
  tone: 'neutral' | 'info' | 'warn' | 'ok' | 'danger';
}

// Lifecycle: draft (grey) → submitted (amber, awaiting HR) → approved (green)
// / flagged (red). 'imported' is a response that arrived via import; 'approved'
// is the legacy pre-HR-review approval, treated the same as 'hr_approved'.
const INTENTION_STATUS_META: Record<string, IntentionStatusMeta> = {
  draft:       { label: 'Draft',     bg: 'bg-ink-100',   text: 'text-ink-900',   tone: 'neutral' },
  submitted:   { label: 'Submitted', bg: 'bg-warn-bg',   text: 'text-warn-fg',   tone: 'warn' },
  imported:    { label: 'Imported',  bg: 'bg-navy-100',  text: 'text-navy-800',  tone: 'info' },
  approved:    { label: 'Approved',  bg: 'bg-ok-bg',     text: 'text-ok-fg',     tone: 'ok' },
  hr_approved: { label: 'Approved',  bg: 'bg-ok-bg',     text: 'text-ok-fg',     tone: 'ok' },
  hr_flagged:  { label: 'Flagged',   bg: 'bg-danger-bg', text: 'text-danger-fg', tone: 'danger' },
};

/** Canonical badge label + token colours for a teaching-intention status. */
export function getIntentionStatusMeta(status: string | null | undefined): IntentionStatusMeta {
  return INTENTION_STATUS_META[status || 'draft'] || INTENTION_STATUS_META.draft;
}

/** Canonical label for a return-intention value ('yes' | 'no' | 'uncertain'). */
export function returnIntentionLabel(value: string | null | undefined): string {
  if (value === 'yes') return 'Returning';
  if (value === 'no') return 'Not returning';
  if (value === 'uncertain') return 'Uncertain';
  return '—';
}

export interface IntentionResponseStats {
  /** Number of staff expected to submit (the rate denominator). */
  expectedCount: number;
  /** Distinct expected staff with a non-draft intention (the rate numerator). */
  submittedCount: number;
  /** Distinct expected staff whose only intention so far is a draft. */
  draftCount: number;
  /** expectedCount - submittedCount. */
  notSubmittedCount: number;
  /** submittedCount / expectedCount as a whole percentage. */
  responseRate: number;
  /** Return-intention breakdown among distinct submitted staff. */
  returning: number;
  notReturning: number;
  uncertain: number;
  /** Sum of target FTE across submitted staff who intend to return. */
  projectedFTE: number;
  /** Raw intention-row counts by status (not de-duplicated) for status displays. */
  byStatus: Record<string, number>;
}

/**
 * Compute the canonical response statistics for a set of intentions against a
 * staff roster. Pass the intentions already scoped to the relevant period/year;
 * this function handles expected-staff filtering, de-duplication and the rate.
 */
export function computeIntentionResponseStats(
  intentions: IntentionLike[],
  staff: StaffLike[],
  staffGroup: 'teaching' | 'all' = 'teaching'
): IntentionResponseStats {
  const expected = staff.filter(s => isExpectedToSubmit(s, staffGroup));
  const expectedCodes = new Set(expected.map(getStaffCode).filter(Boolean));

  // Keep one effective intention per staff code, preferring a submitted one over
  // a draft (a person can have a draft and later a submitted row).
  const byCode = new Map<string, IntentionLike>();
  const byStatus: Record<string, number> = {};
  for (const i of intentions) {
    const status = i.status || 'draft';
    byStatus[status] = (byStatus[status] || 0) + 1;

    const code = (i.staffId || '').toString().trim().toUpperCase();
    if (!code) continue;
    const existing = byCode.get(code);
    if (!existing || (!isSubmittedStatus(existing.status) && isSubmittedStatus(i.status))) {
      byCode.set(code, i);
    }
  }

  const submittedCodes = new Set<string>();
  const draftOnlyCodes = new Set<string>();
  for (const [code, intention] of byCode) {
    if (!expectedCodes.has(code)) continue; // only count expected staff
    if (isSubmittedStatus(intention.status)) submittedCodes.add(code);
    else draftOnlyCodes.add(code);
  }

  let returning = 0;
  let notReturning = 0;
  let uncertain = 0;
  let projectedFTE = 0;
  for (const code of submittedCodes) {
    const intention = byCode.get(code)!;
    if (intention.intentionToReturn === 'yes') {
      returning++;
      projectedFTE += intention.targetFTE || 0;
    } else if (intention.intentionToReturn === 'no') {
      notReturning++;
    } else if (intention.intentionToReturn === 'uncertain') {
      uncertain++;
    }
  }

  const expectedCount = expected.length;
  const submittedCount = submittedCodes.size;

  return {
    expectedCount,
    submittedCount,
    draftCount: draftOnlyCodes.size,
    notSubmittedCount: expectedCount - submittedCount,
    responseRate: expectedCount > 0 ? Math.round((submittedCount / expectedCount) * 100) : 0,
    returning,
    notReturning,
    uncertain,
    projectedFTE,
    byStatus,
  };
}
